import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOnlineStatus } from '../lib/useOnlineStatus'
import { Icon } from './Icon'
import './OfflineBanner.css'

export interface OfflineBannerProps {
  /** What being offline means on this screen. Defaults to the general message. */
  message?: string
}

/**
 * Says so when the phone goes offline (PRD section 19: cached values, money actions
 * wait) and briefly confirms when it is back. The live region is always in the
 * page, so screen readers hear both changes.
 */
export function OfflineBanner({ message }: OfflineBannerProps) {
  const { t } = useTranslation()
  const online = useOnlineStatus()
  const [previous, setPrevious] = useState(online)
  const [backOnline, setBackOnline] = useState(false)
  if (online !== previous) {
    setPrevious(online)
    setBackOnline(online) // true just after reconnecting
  }

  useEffect(() => {
    if (!backOnline) return
    const timer = setTimeout(() => setBackOnline(false), 4000)
    return () => clearTimeout(timer)
  }, [backOnline])

  return (
    <div className="offline-region" role="status" aria-live="polite">
      {!online && (
        <div className="offline-banner">
          <Icon name="cloud_off" />
          <p>
            <strong>{t('kit.offline.title')}</strong> {message ?? t('kit.offline.body')}
          </p>
        </div>
      )}
      {online && backOnline && (
        <div className="offline-banner offline-banner--back">
          <Icon name="check_circle" />
          <p>{t('kit.offline.backOnline')}</p>
        </div>
      )}
    </div>
  )
}
